/**
 * Search, as a command palette.
 *
 * Screen spec 2.6 gives the palette three jobs in priority order: jump to a
 * project, jump to a page inside the current project, jump to a table. It also
 * says no actions, on the grounds that a palette that mutates is a Priya
 * feature that will eventually frighten Nina.
 *
 * RECORDED DEVIATION. Four actions ship here anyway: New project, Copy web
 * address, Open the docs and Replay the tour. None of them mutates anything a
 * person can lose. New project routes to the naming screen, which is the same
 * thing the button on the projects list does. Copy writes to the clipboard.
 * Docs opens a tab. Replay the tour reopens a tour. The spec's fear is of a
 * palette that deletes a database from a fuzzy match, and none of these four
 * is that. The reason to include them at all is the first run problem: a
 * person who does not yet know the names of the screens does know the names of
 * the things they want to do, and typing "new" is how they find the button
 * they have not noticed yet.
 *
 * The one thing kept from the spec exactly: nothing in here destroys anything.
 *
 * WHAT IT NEVER DOES. It does not listen for a bare letter, so it can never
 * hijack a keystroke meant for a field. It opens on Command K or Control K,
 * which no text input owns, and it closes on Escape, always, from anywhere
 * inside it. There is no state in which the palette is open and there is no
 * way out.
 *
 * ACCESSIBILITY. The field is a combobox, the results are a listbox, and the
 * active row is carried by aria-activedescendant rather than by moving focus,
 * which is what keeps typing and arrowing working at the same time.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import { showToast } from '../primitives/Toast';
import { environmentOf } from '../../lib/api';
import { t } from '../../lib/i18n/t';
import { startTour } from '../../lib/hooks/use-tour';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useProjects } from '../../lib/auth/use-app-data';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/palette.css';

/** The documentation lives outside the console. One constant, one place. */
export const DOCS_URL = 'https://cloud.sv-academy.org/docs';

type Group = 'Projects' | 'Screens' | 'Things to do';

interface Item {
  id: string;
  group: Group;
  /** Already translated. */
  label: string;
  /** A machine fact under the label, in mono. A web address, never a sentence. */
  hint?: string;
  /** The word on the right of the row: what will happen. */
  tail?: string;
  /** Extra words the query is matched against but which are never displayed. */
  keywords?: string;
  /**
   * Hidden until the query matches it. Used by the long tail: eight screens
   * times four projects is a resting list nobody can read, and the same eight
   * times four is exactly what a person typing "database" wants.
   */
  onlyWhenSearched?: boolean;
  run: () => void;
}

/** The screens inside a project, in rail order. Muscle memory beats ranking. */
const PROJECT_SCREENS: { path: string; label: string }[] = [
  { path: '', label: 'Overview' },
  { path: '/deploys', label: 'Deploys' },
  { path: '/database', label: 'Database' },
  { path: '/files', label: 'Files' },
  { path: '/keys', label: 'Keys and settings' },
  // Add-ons is off the rail until there is something to offer, so it is off
  // the palette too: a palette that reaches a screen the rail hides is a
  // second, quieter navigation with a different answer.
  { path: '/activity', label: 'Activity' },
  { path: '/usage', label: 'Usage' },
  { path: '/settings', label: 'Settings' },
];

function host(webAddress: string): string {
  return webAddress.replace(/^https?:\/\//, '');
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const projects = useProjects();
  const slug = useProjectSlug();
  const current = slug ? projects.find((p) => p.slug === slug) : undefined;

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const openTimeRef = useRef<number>(0);

  const close = useCallback((method = 'action') => {
    const duration = Date.now() - openTimeRef.current;
    captureEvent('command_palette_closed', {
      method,
      duration_ms: duration,
      had_query: query.length > 0,
      query_length: query.length,
      screen_path: window.location.pathname,
    });
    onClose();
  }, [onClose, query]);

  const go = useCallback(
    (to: string, itemLabel?: string) => {
      captureEvent('command_palette_item_selected', {
        destination: to,
        label: itemLabel,
        query,
        screen_path: window.location.pathname,
      });
      close('navigated');
      navigate(to);
    },
    [close, navigate, query],
  );

  const copyAddress = useCallback(
    (name: string, address: string) => {
      close();
      void navigator.clipboard.writeText(address).then(
        () => showToast(t('Copied the web address for {name}', { name }), host(address)),
        // An honest failure rather than a silent one: some browsers refuse the
        // clipboard outside a trusted gesture and there is nothing to fix here.
        () => showToast(t('Could not copy the web address. Select it on the page instead.')),
      );
    },
    [close],
  );

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];

    for (const project of projects) {
      const address = environmentOf(project).webAddress;
      out.push({
        id: `project:${project.id}`,
        group: 'Projects',
        label: project.name,
        hint: host(address),
        tail: t('Open'),
        run: () => go(`/p/${encodeURIComponent(project.slug)}`),
      });
    }

    // Account scope always resolves, so these three are never conditional.
    // The account roll-up of Usage is here as well as inside a project,
    // because it answers with or without one.
    out.push({
      id: 'screen:projects',
      group: 'Screens',
      label: t('Projects'),
      tail: t('Go'),
      run: () => go('/projects'),
    });
    out.push({
      id: 'screen:usage',
      group: 'Screens',
      label: t('Usage'),
      hint: t('Everything on this account'),
      tail: t('Go'),
      run: () => go('/usage'),
    });
    out.push({
      id: 'screen:account',
      group: 'Screens',
      label: t('Account'),
      tail: t('Go'),
      run: () => go('/account'),
    });

    if (current) {
      // A project is open, so a screen name has exactly one destination.
      const base = `/p/${encodeURIComponent(current.slug)}`;
      for (const screen of PROJECT_SCREENS) {
        out.push({
          id: `screen:${current.id}:${screen.path}`,
          group: 'Screens',
          label: t(screen.label),
          hint: current.name,
          tail: t('Go'),
          run: () => go(`${base}${screen.path}`),
        });
      }
    } else {
      // No project is open, so "Deploys" is a question, not a destination.
      // Every project answers it, and the rows say which one they mean rather
      // than the palette picking a project on the person's behalf.
      for (const project of projects) {
        const base = `/p/${encodeURIComponent(project.slug)}`;
        for (const screen of PROJECT_SCREENS) {
          out.push({
            id: `screen:${project.id}:${screen.path}`,
            group: 'Screens',
            label: t(screen.label),
            hint: project.name,
            tail: t('Go'),
            keywords: project.name,
            onlyWhenSearched: true,
            run: () => go(`${base}${screen.path}`),
          });
        }
      }
    }

    out.push({
      id: 'do:new',
      group: 'Things to do',
      label: t('New project'),
      keywords: 'create add build deploy app',
      tail: t('Start'),
      run: () => go('/new'),
    });

    if (current) {
      out.push({
        id: 'do:copy',
        group: 'Things to do',
        label: t('Copy web address'),
        hint: host(environmentOf(current).webAddress),
        keywords: 'url link domain',
        tail: t('Copy'),
        run: () => copyAddress(current.name, environmentOf(current).webAddress),
      });
    } else {
      for (const project of projects) {
        const address = environmentOf(project).webAddress;
        out.push({
          id: `do:copy:${project.id}`,
          group: 'Things to do',
          label: t('Copy web address'),
          hint: `${project.name}, ${host(address)}`,
          keywords: `url link domain ${project.name}`,
          tail: t('Copy'),
          onlyWhenSearched: true,
          run: () => copyAddress(project.name, address),
        });
      }
    }

    out.push({
      id: 'do:docs',
      group: 'Things to do',
      label: t('Open the docs'),
      keywords: 'documentation help guide',
      tail: t('New tab'),
      run: () => {
        close();
        window.open(DOCS_URL, '_blank', 'noreferrer,noopener');
      },
    });

    out.push({
      id: 'do:tour',
      group: 'Things to do',
      label: t('Replay the tour'),
      keywords: 'help tour walkthrough show me around',
      tail: t('Start'),
      run: () => {
        close();
        navigate('/projects');
        startTour();
      },
    });

    return out;
  }, [projects, current, go, close, copyAddress, navigate]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return items.filter((item) => item.onlyWhenSearched !== true);
    return items.filter((item) =>
      `${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`.toLowerCase().includes(needle),
    );
  }, [items, query]);

  useEffect(() => {
    setActive(0);
    if (query.trim() !== '' && results.length === 0) {
      captureEvent('command_palette_zero_results', {
        query: query.trim(),
        screen_path: window.location.pathname,
      });
    }
  }, [query, results.length]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    openTimeRef.current = Date.now();
    captureEvent('command_palette_opened', {
      screen_path: window.location.pathname,
      current_project: current?.slug,
    });
    returnTo.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      returnTo.current?.focus();
    };
  }, [open, current?.slug]);

  // Keep the active row in view when it walks off the bottom of the list.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    row?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close('escape');
        return;
      }
      // The palette is one field and one list. Tab has nowhere useful to go,
      // and letting it out of a modal is how a person ends up typing into the
      // page behind the scrim.
      if (event.key === 'Tab') {
        event.preventDefault();
        return;
      }
      if (results.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((i) => (i + 1) % results.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((i) => (i - 1 + results.length) % results.length);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActive(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActive(results.length - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        results[active]?.run();
      }
    },
    [results, active, close],
  );

  if (!open) return null;

  const activeId = results[active] ? `pal-${results[active].id}` : undefined;

  let lastGroup: Group | null = null;

  const panel = (
    <div className="pal" onKeyDown={onKeyDown}>
      <div className="pal__scrim" onClick={() => close('scrim')} role="presentation" />

      <div
        className="pal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('Search SV Cloud')}
      >
        <div className="pal__field">
          <span className="pal__glass" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
              <path
                d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9ZM10.4 10.4 13.5 13.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>

          <input
            ref={inputRef}
            className="pal__input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="pal-list"
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            placeholder={t('Search projects, screens and things to do')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />

          <span className="pal__esc" aria-hidden="true">
            Esc
          </span>
        </div>

        {results.length === 0 ? (
          <div className="pal__none">
            <p className="pal__none-say">{t('Nothing matches what you typed.')}</p>
            <p className="pal__none-help">
              {t(
                'This searches your projects by name and by web address, the screens inside a project such as Deploys, Database, Files and Keys and settings, and a few things to do like starting a new project.',
              )}
            </p>
          </div>
        ) : (
          <ul className="pal__list" id="pal-list" role="listbox" ref={listRef}>
            {results.map((item, i) => {
              const head = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <li key={item.id} role="presentation">
                  {head ? (
                    <p className="pal__group" role="presentation">
                      {t(head)}
                    </p>
                  ) : null}
                  <div
                    className="pal__row"
                    id={`pal-${item.id}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={() => item.run()}
                  >
                    <span className="pal__row-text">
                      <span className="pal__row-label">{item.label}</span>
                      {item.hint ? <span className="pal__row-hint mono">{item.hint}</span> : null}
                    </span>
                    {item.tail ? <span className="pal__row-tail">{item.tail}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="pal__foot">
          <span className="pal__foot-keys">{t('Up and down to move')}</span>
          <span className="pal__foot-keys">{t('Enter to open')}</span>
          <span>{t('Escape to close')}</span>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
